import { FC, useEffect, useState } from "react";
import { OverallScore, UserSearchItem } from "src/components";
import { Button } from "src/components/ui/button";
import AlertModal from "src/modals/AlertModal/AlertModal";
import { HelpCircle, AlertTriangle } from "lucide-react";
import { ObservationQuestionList, SubmittedForm } from "src/containers";
import { FormProvider, useFormState, useWatch } from "react-hook-form";
import useObservationForm from "./useObservationForm";
import { useNavigate } from "react-router-dom";
import PausedForm from "../PausedForm/PausedForm";
import QuitForm from "../QuitForm/QuitForm";
import JsonDebug from "src/utils/PreetyJSON";
import WebViewMessageEventEmitter from "src/WebViewEventEmitter";
import StoreSearchItem from "src/components/StoreSearchItem/StoreSearchItem";
import { ReportAnswerModal } from "src/modals";
import { useFormInstance } from "src/hooks/useObservationForm";
import TrophyIcon from "src/assets/icon/trophy.svg?react";
import { whatGreatLooksLikeURL } from "./whatGreatLooksLikeURL";
import { checkObservationFormType } from "src/utils/checkObservationFormType";
import useSingleStoreUser from "src/hooks/useSingleStoreUser/useSingleStoreUser";
import useStore from "src/hooks/useStoreId/useStoreId";
import { SubmitObservationsFormButton } from "src/features/observations/components/SubmitObservationFormButton";

interface Props {
	questions: ObservationQuestion[];
	formId: number;
	formName: string;
	observationId?: number;
	userId?: number | string;
	storeId?: number | string;
	observationType: IObservationType;
}

const ObservationForm: FC<Props> = ({
	questions,
	formId,
	formName,
	observationId,
	userId,
	storeId,
	observationType
}) => {
	const navigate = useNavigate();

	const [visibleQuestions, setVisibleQuestions] = useState<
		ObservationQuestion[]
	>([]);

	const [isPaused, setIsPaused] = useState(false);
	const [isQuit, setIsQuit] = useState(false);

	// Modal states
	const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
	const [isShowPreviousAnswersModalOpen, setIsShowPreviousAnswersModalOpen] =
		useState(false);
	const [isQuitOrPauseModalOpen, setIsQuitOrPauseModalOpen] = useState(false);

	const { data: storeUser } = useSingleStoreUser(userId, {
		enabled:
			Boolean(userId) && checkObservationFormType(observationType) === "user"
	});
	const { data: store } = useStore(storeId, {
		enabled:
			Boolean(storeId) && checkObservationFormType(observationType) === "store"
	});

	const {
		methods,
		submitObversation,
		previousObservationsCompleted,
		quitObservation,
		isDeleteFormInstanceSuccess,
		hasCompleteObservationError,
		isCompleteObservationLoading,
		isCompleteObservationSuccess
	} = useObservationForm({
		questions,
		formId,
		observationId,
		observationType,
		storeUser,
		store,
		visibleQuestions
	});

	const { control, setValue, reset } = methods;

	const selectedSearchItem = useWatch({
		name: "selectedSearchItem",
		defaultValue: { name: "" },
		control
	});

	const overallScore = useWatch({
		name: "overallScore",
		defaultValue: 0,
		control
	});

	const { isSubmitSuccessful } = useFormState({
		control
	});

	const onSubmit = () => {
		submitObversation();
	};

	const onQuit = () => {
		setIsQuitOrPauseModalOpen(false);
		quitObservation();
		setIsQuit(true);
	};

	const onPause = () => {
		setIsQuitOrPauseModalOpen(false);
		setIsPaused(true);
	};

	const showSubmittedForm =
		isSubmitSuccessful &&
		!isPaused &&
		!hasCompleteObservationError &&
		!isCompleteObservationLoading &&
		selectedSearchItem.name;

	const allFields = useWatch({ control });

	const resetFormValues = () => {
		reset();
		setIsQuit(false);
		setIsPaused(false);
	};

	const onObserveAnotherUser = () => {
		resetFormValues();
		navigate(`/observations/submissions/${formId}`);
	};

	const handleObserveAnotherUserClick = () => {
		if (window.ReactNativeWebView) {
			return WebViewMessageEventEmitter.emit("CLOSE_WEBVIEW");
		}

		resetFormValues();
		navigate(`/observations/submissions/${formId}`);
	};

	const handleRedirectAndResetFormClick = () => {
		if (window.ReactNativeWebView) {
			return WebViewMessageEventEmitter.emit("CLOSE_WEBVIEW");
		}

		resetFormValues();
		navigate("/observations/submissions");
	};

	const { refetch } = useFormInstance(observationId, { enabled: false });

	const getOverallScore = async () => {
		const { data: formInstance } = await refetch();

		if (formInstance) {
			setValue(
				"overallScore",
				formInstance?.overall_score ? formInstance.overall_score : 0
			);
		}
	};

	useEffect(() => {
		if (observationType !== "GREAT_CONVERSATION") return;

		if (observationId && String(observationId) !== "create-observation") {
			// Get overall score when the user resumes an observation
			getOverallScore();
		}
	}, [observationId]);

	useEffect(() => {
		if (storeUser && checkObservationFormType(observationType) === "user")
			setValue("selectedSearchItem", storeUser);

		if (store && checkObservationFormType(observationType) === "store")
			setValue("selectedSearchItem", store);
	}, [storeUser, store]);

	useEffect(() => {
		if (isCompleteObservationSuccess) setIsSubmitModalOpen(false);
	}, [isCompleteObservationSuccess]);

	return (
		<>
			{!isQuit && !isPaused && (
				<h1
					className={`text-2xl font-semibold ${observationType === "GREAT_CONVERSATION" ? "mb-6" : "mb-8"}`}
				>
					{formName}
				</h1>
			)}

			<JsonDebug data={allFields} />

			{isDeleteFormInstanceSuccess && isQuit && (
				<QuitForm
					formName={formName}
					observedUser={selectedSearchItem.name}
					resetForm={handleObserveAnotherUserClick}
					redirectAndResetForm={handleRedirectAndResetFormClick}
				/>
			)}

			{isPaused && selectedSearchItem.name && observationId && (
				<PausedForm
					formName={formName}
					observedUser={selectedSearchItem.name}
					onObserveAnotherUser={handleObserveAnotherUserClick}
					resume={() => {
						setIsPaused(false);
						navigate(
							checkObservationFormType(observationType) === "user"
								? `/observations/submissions/${formId}/user/${selectedSearchItem.id}/${observationId}`
								: `/observations/submissions/${formId}/store/${selectedSearchItem.id}/${observationId}`,
							{
								replace: true
							}
						);
					}}
				/>
			)}

			{showSubmittedForm && isCompleteObservationSuccess && (
				<SubmittedForm
					resetForm={reset}
					onObserveAnotherUser={onObserveAnotherUser}
					onSelectAnotheForm={handleRedirectAndResetFormClick}
					observedUser={selectedSearchItem.name}
				/>
			)}

			{!isCompleteObservationSuccess && !isPaused && !isQuit && (
				<>
					{observationType === "GREAT_CONVERSATION" &&
						selectedSearchItem.id &&
						observationId && (
							<Button
								onClick={() => window.open(`${whatGreatLooksLikeURL}`)}
								variant="outline"
								className="mb-4"
							>
								<TrophyIcon />
								<span className="ml-2">What Great looks like</span>
							</Button>
						)}

					<FormProvider {...methods}>
						<form>
							<div className="bg-white mt-6 mb-6 px-4 py-4 rounded-xl border border-transparent">
								<div className="flex flex-col md:flex-row md:justify-between md:items-center">
									{checkObservationFormType(observationType) === "user" && (
										<div className="w-full">
											{selectedSearchItem.id && (
												<UserSearchItem
													user={selectedSearchItem as StoreUser}
													icon={null}
												/>
											)}
										</div>
									)}

									{checkObservationFormType(observationType) === "store" && (
										<div className="w-full">
											{selectedSearchItem.id && (
												<StoreSearchItem
													store={selectedSearchItem as Store}
													icon={null}
												/>
											)}
										</div>
									)}

									{observationId && selectedSearchItem.id && (
										<Button
											type="button"
											variant="outline"
											className="bg-white mt-4 md:mt-0 w-fit md:w-[200px] md:min-w-[200px]"
											disabled={
												previousObservationsCompleted.isInitialLoading ||
												!previousObservationsCompleted.data?.length
											}
											onClick={() => setIsShowPreviousAnswersModalOpen(true)}
										>
											{previousObservationsCompleted.isInitialLoading
												? "Loading..."
												: "Show previous answers"}
										</Button>
									)}
								</div>
							</div>

							{selectedSearchItem.id && observationId && (
								<>
									<ObservationQuestionList
										observationType={observationType}
										questions={questions}
										visibleQuestions={visibleQuestions}
										onVisibleQuestionsChange={setVisibleQuestions}
									/>

									{observationType === "GREAT_CONVERSATION" && (
										<OverallScore score={overallScore} />
									)}

									<div className="flex flex-col md:flex-row gap-4 md:gap-0 my-10 md:justify-between">
										<Button
											type="button"
											variant="outline"
											onClick={() => setIsQuitOrPauseModalOpen(true)}
										>
											Quit or pause observation
										</Button>

										<SubmitObservationsFormButton
											onClick={() => setIsSubmitModalOpen(true)}
											visibleQuestions={visibleQuestions}
										/>
									</div>
								</>
							)}
						</form>
					</FormProvider>
				</>
			)}

			{/* Submit Observation Modal */}
			<AlertModal
				onClose={() => setIsSubmitModalOpen(false)}
				isOpen={isSubmitModalOpen}
				ModalControllers={
					<>
						<Button
							variant="outline"
							size="lg"
							onClick={() => setIsSubmitModalOpen(false)}
						>
							Back to form
						</Button>

						<Button
							size="lg"
							disabled={isCompleteObservationLoading}
							onClick={onSubmit}
						>
							{isCompleteObservationLoading
								? "Loading..."
								: hasCompleteObservationError
									? "Retry"
									: "Submit"}
						</Button>
					</>
				}
			>
				<AlertTriangle size={48} data-testid="warning-icon" />
				<div className="mt-4">
					{hasCompleteObservationError && (
						<div className="flex flex-1 justify-center">
							<div className="flex flex-col items-center gap-2 my-8">
								<p className="font-heading text-lg font-medium">
									The form has failed to submit.
								</p>
								<p>Please retry below, or go back to the form and try again.</p>
							</div>
						</div>
					)}
					{!hasCompleteObservationError && (
						<div className="flex flex-1 justify-center">
							<p className="font-heading text-lg font-medium">
								Are you sure you want to submit this form?
							</p>
						</div>
					)}
				</div>
			</AlertModal>

			{/* Show previous answers modal */}
			{previousObservationsCompleted.data && (
				<ReportAnswerModal
					isOpen={isShowPreviousAnswersModalOpen}
					onClose={() => setIsShowPreviousAnswersModalOpen(false)}
					observations={previousObservationsCompleted.data}
					form={{ name: formName, observation_type: observationType }}
				/>
			)}

			{/* Quit Or Pause Modal */}
			<AlertModal
				onClose={() => setIsQuitOrPauseModalOpen(false)}
				isOpen={isQuitOrPauseModalOpen}
				ModalControllers={
					<>
						<Button variant="outline" size="lg" onClick={onQuit}>
							Quit
						</Button>

						<Button size="lg" onClick={onPause}>
							Pause
						</Button>
					</>
				}
			>
				<HelpCircle size={48} data-testid="help-icon" />
				<div className="mt-4">
					<p className="font-heading text-lg font-medium">
						Quit or pause this observation?
					</p>
					<p className="text-sm text-foreground/60">
						Pausing an observation will save the current answers for this
						employee. If not resumed within 24 hours the observation will be
						automatically closed.
					</p>
				</div>
			</AlertModal>
		</>
	);
};

export default ObservationForm;
